import React, { useState, useEffect } from 'react';
import { IMaskInput } from 'react-imask';
import type { FieldProps } from '../types';

/**
 * TextField Component
 *
 * Renders text input with optional masking support.
 * Supports: text, email, tel, url
 */
export const TextField: React.FC<FieldProps> = ({
  element,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  theme,
}) => {
  const properties = element.properties;
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    onChange(newValue);
  };

  const inputType = properties?.name?.toLowerCase().includes('email')
    ? 'email'
    : properties?.name?.toLowerCase().includes('phone')
    ? 'tel'
    : properties?.name?.toLowerCase().includes('url')
    ? 'url'
    : 'text';

  // Check if masking is enabled
  const maskConfig = properties?.inputMask;
  const useMask = maskConfig?.enabled && maskConfig.maskType !== 'custom';

  // Get mask pattern based on type
  const getMaskPattern = (): string => {
    if (!maskConfig) return '';

    switch (maskConfig.maskType) {
      case 'phone':
        return '(000) 000-0000';
      case 'ssn':
        return '000-00-0000';
      case 'date':
        return '00/00/0000';
      case 'currency':
        return '$0,000.00';
      case 'zip':
        return '00000';
      case 'zipPlus4':
        return '00000-0000';
      case 'creditCard':
        return '0000 0000 0000 0000';
      case 'custom':
        return maskConfig.customMask || '';
      default:
        return '';
    }
  };

  const maskPattern = getMaskPattern();

  return (
    <div className="mediform-field mediform-text-field">
      {properties?.label && (
        <label htmlFor={element.id} className="mediform-field-label">
          {properties.label}
          {properties.required && <span className="mediform-required"> *</span>}
        </label>
      )}

      <div className="mediform-field-input">
        {useMask && maskPattern ? (
          <IMaskInput
            id={element.id}
            mask={maskPattern}
            value={localValue}
            onAccept={(value) => handleChange(value)}
            onBlur={onBlur}
            disabled={disabled}
            placeholder={maskConfig?.placeholder || properties?.label}
            lazy={maskConfig?.lazy}
            className={`mediform-input ${error ? 'mediform-input-error' : ''}`}
            style={{
              borderColor: error ? theme?.colors?.error : theme?.colors?.border,
            }}
          />
        ) : (
          <input
            id={element.id}
            type={inputType}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            required={properties?.required}
            minLength={properties?.minLength}
            maxLength={properties?.maxLength}
            pattern={properties?.pattern}
            placeholder={properties?.label}
            className={`mediform-input ${error ? 'mediform-input-error' : ''}`}
            style={{
              borderColor: error ? theme?.colors?.error : theme?.colors?.border,
            }}
          />
        )}
      </div>

      {error && (
        <div className="mediform-field-error" style={{ color: theme?.colors?.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default TextField;
